//@ts-strict-ignore
import React, { useMemo } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { CustomerProductHistory } from '../../graphql/entities';
import Formatters from '../../components/grid-addons/formatters';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { usePrecision } from '../../utils/hooks';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';

const { workspace, type, title } = CustomerProductHistory;

const CUSTOMER_PRODUCT_HISTORY_FIELD_NAMES = [
  'reference',
  'description',
  'latestCostPrice',
  'costUnit.description',
  'stockUnit.description',
  'vatProductCategory.description',
];

const getColumnOverrides = (costPricePrecision: number, sellingPricePrecision: number): IWorkspaceColumnOverride => {
  let columnOverrides: IWorkspaceColumnOverride = {
    latestCostPrice: {
      formatter: 'numeric',
      options: { format: { decimals: costPricePrecision } },
      cellClass: 'rightAlign',
      name: 'Latest Cost',
    },
    valuationCostPrice: {
      formatter: 'numeric',
      options: { format: { decimals: costPricePrecision } },
      cellClass: 'rightAlign',
      name: 'Valuation Cost',
    },
    haggleCostPrice: {
      formatter: 'numeric',
      options: { format: { decimals: costPricePrecision } },
      cellClass: 'rightAlign',
      name: 'Haggle Cost',
    },
    'sellingPrices.minimumPrice': {
      formatter: 'numeric',
      options: { format: { decimals: sellingPricePrecision } },
      cellClass: 'rightAlign',
      name: 'Selling Prices - Minimum Price',
    },
    reference: {
      formatter: Formatters.PrimaryFieldFormatter,
    },
    'channels.description': {
      formatter: Formatters.ChannelFormatter,
      name: 'Channels',
      sortable: false,
    },
    'costUnit.description': {
      name: 'Cost Unit',
    },
    'stockUnit.description': {
      name: 'Stock Unit',
    },
    'vatProductCategory.description': {
      name: 'VAT Category',
    },
  };

  for (let i = 1; i < 11; i++) {
    columnOverrides[`sellingPrices.sellingPrice${i}`] = {
      name: `Selling Prices - Selling Price ${i}`,
      options: { format: { decimals: sellingPricePrecision } },
    };
  }

  return columnOverrides;
};

/**
 * Safety Data Sheet Product History. (Gets all sales order lines for the Customer /w Data Sheets against them, and lists those products).
 */
const CustomerProductHistoryWorkspace = (props) => {
  const costPricePrecision = usePrecision('cost');
  const sellingPricePrecision = usePrecision('sales');

  const columnOverrides: IWorkspaceColumnOverride = useMemo(() => {
    return getColumnOverrides(costPricePrecision, sellingPricePrecision);
  }, [costPricePrecision, sellingPricePrecision]);

  const workspaceProps = useWorkspace(props, CustomerProductHistory, {
    columns: CUSTOMER_PRODUCT_HISTORY_FIELD_NAMES,
    columnOverrides: columnOverrides,
    initialSortColumn: 'reference',
  });

  return (
    <Workspace
      ref={props.setWorkspaceRef}
      entityType={type}
      title={title}
      defaultQuery={workspace}
      {...workspaceProps}
      {...props}
      entity={CustomerProductHistory}
    />
  );
};

export default CustomerProductHistoryWorkspace;
